import { action, computed, flow, makeObservable, observable, reaction } from 'mobx';

import { isLocalNewTrajectoryId } from '../../service/entities';
import type { TrajectoryService } from '../../service/trajectory-service';
import type { TCtcRowResponse } from '../../types/calculate-ctc';
import { getApiErrorCode, OperationAbortedError } from '../../utils/errors';

import { EMPTY_CTC_MAPPED_CHART, mapCtcRowsToChart } from './trajectory-2d-charts/map-ctc-rows-to-chart';
import type { Trajectory2dMappedChart } from './trajectory-2d-charts/map-trajectory-points-by-config';
import type { TrajectoryGraphStore } from './trajectory-graph.store';

type TCtcRequestKey = {
  wellId: number;
  activeTrajectoryId: number;
  offsetTrajectoryIds: number[];
};

function serializeCtcRequestKey(key: TCtcRequestKey | null): string | null {
  if (!key) {
    return null;
  }

  return `${key.wellId}|${key.activeTrajectoryId}|${key.offsetTrajectoryIds.join(',')}`;
}

export class TrajectoryCtcStore {
  private readonly trajectoryService: TrajectoryService;
  private readonly graphStore: TrajectoryGraphStore;
  private abortController: AbortController | null = null;

  @observable isLoading = false;
  @observable errorCode: string | null = null;
  @observable.ref rows: TCtcRowResponse[] = [];

  constructor(trajectoryService: TrajectoryService, graphStore: TrajectoryGraphStore) {
    this.trajectoryService = trajectoryService;
    this.graphStore = graphStore;

    makeObservable(this);
  }

  @computed
  private get referenceTrajectories() {
    return this.graphStore.graphTrajectories.filter(
      (trajectory) => trajectory.role === 'reference' && !isLocalNewTrajectoryId(trajectory.id),
    );
  }

  @computed
  get crsExcludedTrajectories() {
    return this.referenceTrajectories.filter((trajectory) => trajectory.spatialExcludeReason === 'crsMismatch');
  }

  @computed
  get offsetTrajectoryIds(): number[] {
    return this.referenceTrajectories
      .filter((trajectory) => trajectory.spatialExcludeReason !== 'crsMismatch')
      .map((trajectory) => trajectory.id)
      .sort((left, right) => left - right);
  }

  @computed
  get hasOffsetTrajectories(): boolean {
    return this.offsetTrajectoryIds.length > 0;
  }

  @computed
  get mappedChart(): Trajectory2dMappedChart {
    if (this.isLoading) {
      return EMPTY_CTC_MAPPED_CHART;
    }

    return mapCtcRowsToChart(
      this.rows,
      this.graphStore.graphTrajectories,
      this.crsExcludedTrajectories,
      this.graphStore.activeLegendLabel,
      this.errorCode,
    );
  }

  @computed
  private get requestKey(): TCtcRequestKey | null {
    if (this.trajectoryService.planOrFact !== 'fact') {
      return null;
    }

    if (this.trajectoryService.isWellboreTrajectoriesLoading) {
      return null;
    }

    if (this.graphStore.chart2dVisibility.ctcMd === false) {
      return null;
    }

    const wellId = this.trajectoryService.wellId;
    const activeTrajectoryId = this.trajectoryService.selectedTrajectoryId;

    if (wellId == null || activeTrajectoryId == null || isLocalNewTrajectoryId(activeTrajectoryId)) {
      return null;
    }

    if (this.offsetTrajectoryIds.length === 0) {
      return null;
    }

    return {
      wellId,
      activeTrajectoryId,
      offsetTrajectoryIds: this.offsetTrajectoryIds,
    };
  }

  init = (): VoidFunction => {
    const disposeReaction = reaction(
      () => serializeCtcRequestKey(this.requestKey),
      () => {
        this.syncCtc();
      },
      { fireImmediately: true },
    );

    return () => {
      disposeReaction();
      this.dispose();
    };
  };

  @action.bound
  private dispose(): void {
    this.abortController?.abort();
    this.abortController = null;
    this.rows = [];
    this.errorCode = null;
    this.isLoading = false;
  }

  @flow.bound
  private *syncCtc() {
    this.abortController?.abort();

    const key = this.requestKey;

    if (key == null) {
      this.rows = [];
      this.errorCode = null;
      this.isLoading = false;
      this.abortController = null;
      return;
    }

    const controller = new AbortController();
    this.abortController = controller;
    this.rows = [];
    this.errorCode = null;
    this.isLoading = true;

    try {
      const response: Awaited<ReturnType<TrajectoryService['calculateCtc']>> =
        yield this.trajectoryService.calculateCtc(
          {
            activeTrajectoryId: key.activeTrajectoryId,
            offsetTrajectoryIds: key.offsetTrajectoryIds,
          },
          controller.signal,
        );

      if (this.abortController !== controller) {
        return;
      }

      this.rows = response.rows ?? [];
      this.isLoading = false;
    } catch (error) {
      if (error instanceof OperationAbortedError || this.abortController !== controller) {
        return;
      }

      this.rows = [];
      this.errorCode = getApiErrorCode(error);
      this.isLoading = false;
    }
  }
}
